import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import BorderAll from "@mui/icons-material/BorderAll";
import BorderStyle from "@mui/icons-material/BorderStyle";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import {
  Box,
  Button,
  FormControlLabel,
  Menu,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { usePopover } from "../../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../../generic/components/colour-picker/colour-picker";
import { SET_DATA_TABLE_BORDER_COMMAND } from "../../../types/commands";

import type { Border } from "../../../types/data-table-border";
import type { FC } from "react";

export const DataTableBorder: FC<{
  nodeKey: string;
  border: Border;
}> = ({ nodeKey, border: initialBorder }) => {
  const { anchorRef, handleToggle, handleClose, open } = usePopover<HTMLButtonElement>();
  const [border, setTempBorder] = useState<{ value: Border; skipHistory: boolean }>({
    value: initialBorder,
    skipHistory: false,
  });
  const [editor] = useLexicalComposerContext();

  const handleBorderChange = useCallback(
    (
      { width, style, colour }: { width?: number; style?: string; colour?: string },
      skipHistory?: boolean,
    ) => {
      editor.dispatchCommand(SET_DATA_TABLE_BORDER_COMMAND, {
        nodeKey,
        border: { width, style, colour },
        skipHistory: skipHistory ?? false,
      });
      setTempBorder((current) => ({
        value: {
          width: width ?? current.value.width,
          style: style ?? current.value.style,
          colour: colour ?? current.value.colour,
        },
        skipHistory: skipHistory ?? false,
      }));
    },
    [setTempBorder, editor, nodeKey],
  );

  const handleBorderColourChange = useCallback(
    (colour: string, skipHistory?: boolean) => {
      handleBorderChange({ colour }, skipHistory);
    },
    [handleBorderChange],
  );

  const handleBorderStyleChange = useCallback(
    (style: string) => {
      handleBorderChange({ style }, false);
    },
    [handleBorderChange],
  );

  const handleBorderWidthChange = useCallback(
    (width: number, skipHistory?: boolean) => {
      handleBorderChange({ width }, skipHistory);
    },
    [handleBorderChange],
  );

  return (
    <>
      <Button
        data-analytics-id="lexical-table-menu-borders"
        onClick={handleToggle}
        ref={anchorRef}
        endIcon={
          <KeyboardArrowDown
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        }
        sx={{ color: "text.secondary" }}
        size="small"
      >
        Borders
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { minWidth: 200 },
          },
        }}
      >
        <Stack sx={{ p: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} justifyContent="inline-start">
            <BorderAll fontSize="small" />
            <Typography variant="body1" color="text.secondary">
              Border width
            </Typography>
          </Stack>

          <Box sx={{ px: 1 }}>
            <Slider
              value={border.value.width}
              onChange={(_, value) => handleBorderWidthChange(value, true)}
              onChangeCommitted={(_, value) => handleBorderWidthChange(value, false)}
              min={1}
              max={10}
              step={1}
              size="medium"
              valueLabelFormat={(val) => `${val}px`}
              valueLabelDisplay="auto"
            />
          </Box>
          <Stack
            sx={{ mt: 2 }}
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="inline-start"
          >
            <BorderStyle fontSize="small" />
            <Typography variant="body1" color="text.secondary">
              Border style
            </Typography>
          </Stack>
          <RadioGroup
            row
            onChange={(e) => handleBorderStyleChange(e.target.value)}
            value={border.value.style}
            sx={{ px: 1 }}
          >
            <FormControlLabel
              value="solid"
              control={<Radio size="small" />}
              label="Solid"
              sx={{ color: "text.secondary" }}
            />
            <FormControlLabel
              value="dashed"
              control={<Radio size="small" />}
              label="Dashed"
              sx={{ color: "text.secondary" }}
            />
            <FormControlLabel
              value="dotted"
              control={<Radio size="small" />}
              label="Dotted"
              sx={{ color: "text.secondary" }}
            />
          </RadioGroup>

          <Box sx={{ mt: 2 }}>
            <BorderColourPicker colour={border.value.colour} setColour={handleBorderColourChange} />
          </Box>
        </Stack>
      </Menu>
    </>
  );
};

const BorderColourPicker: FC<{
  colour: string;
  setColour: (colour: string, skipHistory?: boolean) => void;
}> = ({ colour, setColour }) => {
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button
        variant="text"
        data-analytics-id="lexical-table-menu-border-colour"
        onClick={popover.handleToggle}
        ref={popover.anchorRef}
        sx={{ color: "text.secondary", justifyContent: "flex-start", pl: 1, width: "100%" }}
        startIcon={<PaletteOutlined fontSize="small" />}
      >
        <Typography variant="body1">Border color</Typography>
      </Button>
      <ColourPicker
        popover={popover}
        colour={colour}
        onChange={setColour}
        inputRef={popoverInputRef}
      />
    </>
  );
};
